import React from 'react';
import { Button, Space, Spin, Table, TableProps, Tag, Tooltip, message } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import moment from 'moment/moment';
import { useSelector } from 'react-redux';
import { TableParams } from '../../../../../Utils/TableTypes';
import { isWhiteLabelAdmin } from '../../../../../Utils/whiteLabel';
import { CopyOutlined } from '@ant-design/icons';

const isWhiteLabel = isWhiteLabelAdmin();

interface userDataSource {
  id: number | string;
  name: { name: string; id: string | number };
  company: { name: string; companyId: string | number };
  logbook: { name: string | undefined; id: string | number | undefined };
  role: string;
  isActive: boolean | undefined | null;
  dataSharing: string;
  settings?: { disable_send_integration?: boolean | null } | null;
  publicId: string | number | undefined;
  contractId: string | number | null | undefined;
  username: string | undefined;
  terminated: string;
  edit: { id: number | string; role: string };
  key: React.Key;
}

interface userApiRow {
  id: number | string;
  first_name: string;
  second_name: string;
  company?:
    | {
        name?: string;
        id?: string | number;
      }
    | string
    | null;
  companyId?: string | number;
  logbook?: { name?: string; id?: string | number } | null;
  role: string;
  is_active?: boolean | null;
  publicId?: string | number;
  contract?: { contractId?: string | number | null } | null;
  username?: string;
  terminated?: string;
  settings?: { disable_send_integration?: boolean | null } | null;
}

const UsersTable = ({
  data = [],
  isLoading,
  isFetching,
  refetch,
  handleTableChange,
  tableParams,
  pagination,
}: {
  data: Array<any> | undefined;
  total: number | undefined;
  isLoading: boolean | undefined;
  isFetching: any;
  refetch(): void;
  handleTableChange: TableProps<any>['onChange'];
  tableParams: TableParams;
  pagination: boolean | any;
}) => {
  const { theme } = useSelector((state: any) => state.global);

  const location = useLocation();
  let dispatcher = location.pathname === '/user/dispatcher';

  const copyToClipboard = async (value: string | number | null | undefined) => {
    if (value === undefined || value === null || value === '') {
      message.warning('Public ID is empty');
      return;
    }

    const text = String(value);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      message.success('Public ID copied');
    } catch {
      message.error('Failed to copy Public ID');
    }
  };

  const columns: object[] = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 65,
    },

    {
      title: 'Full name',
      dataIndex: 'name',
      key: 'name',
      render: (val: { name: string; id: string | number }) => (
        <Space size={6}>
          <Link to={dispatcher ? `/user/dispatcher/${val.id}` : `/user/users/${val.id}`}>
            {val?.name}
          </Link>
          <Tooltip title="Copy name">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigator.clipboard.writeText(val?.name ?? '').then(
                  () => message.success('Name copied'),
                  () => message.error('Failed to copy name'),
                );
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: dispatcher ? 'Logbook' : 'Company',
      dataIndex: dispatcher ? 'logbook' : 'company',
      key: dispatcher ? 'logbook' : 'company',
      render: (val: { name: string; companyId: string | number }) => (
        <>
          {dispatcher ? (
            <> {val.name}</>
          ) : (
            <>
              {val.name ? (
                <Link to={`/companies/${val.companyId}`}>{val?.name}</Link>
              ) : (
                <div>Company:</div>
              )}
            </>
          )}
        </>
      ),
    },
    ...(!isWhiteLabel
      ? [
          {
            title: 'Role',
            dataIndex: 'role',
            key: 'role',
            width: 130,
          },
        ]
      : []),
    {
      title: 'Is Active',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (tag: boolean) => (
        <Tag color={tag ? 'geekblue' : 'red'}>{tag ? 'True' : 'False'}</Tag>
      ),
      // onHeaderCell: () => ({
      //     onClick: () => setSort(sort === 'ascend' ? 'descend' :
      //         'ascend'),
      //     sortDirections: ['ascend', 'descend', 'ascend'],
      // }),
    },
    ...(!dispatcher
      ? [
          {
            title: 'Data Sharing',
            dataIndex: 'dataSharing',
            key: 'dataSharing',
            width: 150,
          },
        ]
      : []),
    ...(!dispatcher
      ? [
          {
            title: 'Public ID',
            dataIndex: 'publicId',
            key: 'publicId',
            render: (publicId: string | number) => (
              <Space size={6}>
                <span>{publicId ?? '-'}</span>
                <Tooltip title="Copy Public ID">
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(publicId)}
                    disabled={publicId === undefined || publicId === null || publicId === ''}
                  />
                </Tooltip>
              </Space>
            ),
          },
          {
            title: 'Subscription Type',
            dataIndex: 'contractId',
            key: 'contractId',
            render: (contractId: string | null | undefined) =>
              contractId ? `#${contractId}` : 'Non-Contract',
          },
        ]
      : []),
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Terminated At',
      dataIndex: 'terminated',
      key: 'terminated',
    },
    {
      title: 'Actions',
      dataIndex: 'edit',
      key: 'edit',
      // fixed: 'right',
      width: 150,
      render: (val: { id: number | string; role: string }) => (
        <Space>
          <Link to={dispatcher ? `/user/dispatcher/${val.id}` : `/user/users/${val.id}`}>
            <Button>Edit</Button>
          </Link>
          {/*{ val.role === 'dispatcher' &&*/}
          {/*    <div style={{color: '#00c5ff', cursor: 'pointer'}} onClick={() => {*/}
          {/*        Modal.confirm({*/}
          {/*                title: 'LoadController',*/}
          {/*                icon: <ExclamationCircleOutlined/>,*/}
          {/*                content: 'Do you want to delete this user ?',*/}
          {/*                okText: 'Ok',*/}
          {/*                cancelText: 'cancel',*/}
          {/*                onOk: async () => {*/}
          {/*                    users.deleteUser(val.id)*/}
          {/*                    refetch()*/}
          {/*                }*/}
          {/*            }*/}
          {/*        );*/}
          {/*    }*/}
          {/*    }*/}
          {/*    >*/}
          {/*        Delete*/}
          {/*    </div>*/}
          {/*}*/}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Spin size="large" spinning={isLoading || isFetching}>
        <Table
          style={{ margin: '15px 0 0 0' }}
          onChange={handleTableChange}
          dataSource={data?.map((u: userApiRow, index: number): userDataSource => {
            let create = u?.terminated;
            const obj: userDataSource = {
              id: index + 1,
              name: { name: u?.first_name + ' ' + u?.second_name, id: u.id },
              company: {
                name:
                  typeof u?.company === 'object' && u?.company !== null
                    ? u.company.name || '-'
                    : typeof u?.company === 'string'
                      ? u.company
                      : '-',

                companyId:
                  typeof u?.company === 'object' && u?.company !== null
                    ? (u.company.id ?? u.companyId ?? '')
                    : (u.companyId ?? ''),
              },
              logbook: { name: u?.logbook?.name, id: u.logbook?.id },
              role: u.role.toUpperCase(),
              isActive: u.is_active,
              dataSharing: u?.settings?.disable_send_integration ? 'Not Allowed' : 'Allowed',
              settings: u?.settings,
              publicId: u?.publicId,
              contractId: u?.contract?.contractId,
              username: u?.username,
              terminated: create ? moment(create).format('YYYY-MM-DD, h:mm:ss a') : '',
              edit: { id: u.id, role: u.role },
              key: u.id,
            };
            return obj;
          })}
          columns={columns}
          pagination={pagination}
          scroll={{ x: 'max-content' }}
        />
      </Spin>
    </div>
  );
};

export default UsersTable;
